import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Handshake, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Seo } from "../components/Seo";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { SPONSOR_PACKAGES, IMAGES } from "../data/club";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/15 bg-[#0B1520]/60 px-4 py-3 text-sm text-[#F7F5F0] placeholder-[#5C7285] focus:outline-none focus:border-[#E5AC3C] focus:ring-1 focus:ring-[#E5AC3C] transition-colors";

const logoSrc = (logo) => (logo && logo.startsWith("/assets") ? logo : `${API}/files/${logo}`);

export default function Sponsorship() {
  const [form, setForm] = useState({ name: "", email: "", package: "Match Ball Sponsor", message: "" });
  const [sending, setSending] = useState(false);
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    axios.get(`${API}/sponsors`).then((r) => setSponsors(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        subject: `Sponsorship — ${form.package}`,
        message: form.message || `Interested in: ${form.package}`,
        kind: "sponsorship",
      });
      toast.success(res.data.message);
      setForm({ name: "", email: "", package: "Match Ball Sponsor", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="sponsorship-page">
      <Seo
        title="Sponsorship — Whitley Bay Cricket Club"
        description="Partner with Whitley Bay CC: match ball sponsorship, shirt and kit sponsorship, boundary boards at West Park and junior section partnerships."
      />
      <PageHeader
        eyebrow="Partner with the club"
        title="Sponsorship"
        accent="opportunities."
        description="Put your business at the heart of a thriving community club — and back champions while you do it."
        image={IMAGES.lighthouse}
        testId="sponsorship-header"
      />

      {sponsors.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16" data-testid="partners-section">
          <Reveal>
            <p className="text-xs font-mono-club uppercase tracking-[0.25em] text-[#E5AC3C] mb-3">With thanks to</p>
            <h2 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight mb-8">
              Our club <span className="font-serif-accent font-medium text-[#E5AC3C]">partners</span>
            </h2>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {sponsors.map((s, i) => (
              <Reveal key={s.id} delay={(i % 3) * 0.08}>
                <div className="h-full rounded-2xl border border-white/10 bg-white p-8 flex flex-col items-center justify-center gap-3 text-center" data-testid={`partner-logo-${i}`}>
                  {s.link ? (
                    <a href={s.link} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity" data-testid={`partner-link-${i}`}>
                      <img src={logoSrc(s.logo)} alt={`${s.name} — club partner of Whitley Bay Cricket Club`} className="max-h-20 sm:max-h-24 w-auto mx-auto" loading="lazy" />
                    </a>
                  ) : (
                    <img src={logoSrc(s.logo)} alt={`${s.name} — club partner of Whitley Bay Cricket Club`} className="max-h-20 sm:max-h-24 w-auto mx-auto" loading="lazy" />
                  )}
                  <span className="text-xs font-mono-club uppercase tracking-[0.15em] text-[#5C7285]">{s.name}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {SPONSOR_PACKAGES.map((p, i) => (
          <Reveal key={p.name} delay={(i % 4) * 0.07}>
            <div className="h-full rounded-2xl border border-white/10 bg-[#122030] p-7 hover:border-[#E5AC3C]/40 transition-colors" data-testid={`sponsor-package-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <Handshake size={22} className="text-[#E5AC3C]" />
              <h2 className="mt-4 font-head text-lg font-bold">{p.name}</h2>
              <span className="mt-1 inline-block text-[10px] font-mono-club uppercase tracking-[0.15em] text-[#5C7285]">{p.price}</span>
              <p className="mt-3 text-sm text-[#9BB0C1] leading-relaxed">{p.description}</p>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-24">
        <Reveal>
          <form onSubmit={submit} className="rounded-3xl border border-white/10 bg-[#122030] p-8 sm:p-10" data-testid="sponsorship-form">
            <h2 className="font-head text-xl sm:text-2xl font-bold">Become a sponsor</h2>
            <p className="mt-2 text-sm text-[#9BB0C1]">Tell us which package interests you and the club will be in touch.</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <input required minLength={2} className={inputCls} placeholder="Your name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sponsorship-name-input" />
              <input required type="email" className={inputCls} placeholder="Email address" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sponsorship-email-input" />
            </div>
            <select className={`${inputCls} mt-4`} value={form.package}
              onChange={(e) => setForm({ ...form, package: e.target.value })} data-testid="sponsorship-package-select">
              {SPONSOR_PACKAGES.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
              <option value="Something else">Something else</option>
            </select>
            <textarea rows={4} className={`${inputCls} mt-4`} placeholder="Tell us about your business (optional)" value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="sponsorship-message-input" />
            <button
              type="submit"
              disabled={sending}
              data-testid="sponsorship-submit-button"
              className="mt-6 w-full sm:w-auto bg-[#E5AC3C] hover:bg-[#F3C35B] disabled:opacity-60 text-[#0B1520] font-bold px-8 py-3.5 rounded-full text-sm transition-colors inline-flex items-center gap-2 justify-center"
            >
              {sending ? "Sending…" : <>Send enquiry <ArrowRight size={15} /></>}
            </button>
          </form>
        </Reveal>
        <p className="mt-6 text-center text-xs text-[#5C7285]">
          Sponsor logos are added by the club from the <Link to="/admin" className="text-[#E5AC3C] hover:text-[#F3C35B]" data-testid="sponsorship-admin-link">admin panel</Link>.
        </p>
      </section>
    </main>
  );
}
